import { useState } from "react";
import { Topbar } from "@marekl/adminer/shell";
import {
  Page,
  Card,
  ResponsiveGrid,
  KPI,
  KPIGrid,
  Stat,
  Tabs,
  Alert,
  DescriptionList,
  LoadingState,
  ErrorState,
  DatePicker,
  DateTimePicker,
  TimePicker,
  FormStack,
  DateTimeField,
} from "@marekl/adminer/components";
import { BarChart } from "../../components/BarChart";
import { useDashboardStats } from "../../lib/queries/dashboard";
import { dayjs, type Dayjs } from "@marekl/adminer/datetime";
import { formatNumber } from "@marekl/adminer/format";

const TABS = [
  { id: "7", label: "Posledních 7 dní" },
  { id: "30", label: "Posledních 30 dní" },
];

function formatBucketLabel(date: string, days: number): string {
  const d = dayjs(date);
  return days <= 7 ? d.format("dd D.M.") : d.format("D.M.");
}

export function DashboardPage() {
  const [days, setDays] = useState<"7" | "30">("7");
  const stats = useDashboardStats(Number(days));

  const [demoDate, setDemoDate] = useState<Dayjs | null>(dayjs());
  const [demoDateTime, setDemoDateTime] = useState<Dayjs | null>(dayjs());
  const [demoTime, setDemoTime] = useState<Dayjs | null>(
    dayjs().hour(9).minute(30),
  );

  const totalActivity =
    stats.data?.activity.reduce((s, b) => s + b.count, 0) ?? 0;

  return (
    <>
      <Topbar crumbs={[{ label: "Přehled" }]} />
      <Page.Content stack gap="lg">
        <Page.Header
          title="Přehled"
          subtitle="adminer demo — výchozí bod administrace"
        />

        <Alert variant="info" title="Vítejte v Adminer Demo">
          Toto je generická šablona admin panelu. Pomocí navigace vlevo můžete
          spravovat <strong>Kategorie</strong>, <strong>Položky</strong> a{" "}
          <strong>Poznámky</strong>. Manažeři mají přístup k auditnímu logu,
          superadmini ke správě uživatelů.
        </Alert>

        {stats.isLoading ? (
          <LoadingState label="Načítám statistiky…" />
        ) : stats.error ? (
          <ErrorState
            error={stats.error}
            title="Nepodařilo se načíst statistiky."
          />
        ) : stats.data ? (
          <>
            <Card>
              <Card.Header title="Klíčové ukazatele" subtitle="aktuální stav" />
              <KPIGrid>
                <KPI
                  label="Kategorie"
                  value={formatNumber(stats.data.counts.categories)}
                />
                <KPI
                  label="Položky"
                  value={formatNumber(stats.data.counts.items)}
                />
                <KPI
                  label="Poznámky"
                  value={formatNumber(stats.data.counts.notes)}
                />
                <KPI
                  label="Uživatelé"
                  value={formatNumber(stats.data.counts.users)}
                />
              </KPIGrid>
            </Card>

            <Card>
              <Card.Header
                title="Aktivita"
                subtitle="počet záznamů v auditním logu"
                actions={
                  <Tabs
                    aria-label="Časové okno"
                    tabs={TABS}
                    value={days}
                    onChange={(v) => setDays(v as "7" | "30")}
                  />
                }
              />
              <BarChart
                aria-label="Aktivita za zvolené období"
                valueFormatter={(v) => formatNumber(v)}
                data={stats.data.activity.map((b) => ({
                  label: formatBucketLabel(b.date, Number(days)),
                  value: b.count,
                  tooltipLabel: dayjs(b.date).format("dddd D.M.YYYY"),
                }))}
              />
              <Stat
                label={`Celkem za ${days} dní`}
                value={formatNumber(totalActivity)}
              />
              <Stat
                label="Průměr na den"
                value={formatNumber(Math.round(totalActivity / Number(days)))}
              />
              <Stat
                label="Maximum za den"
                value={formatNumber(
                  Math.max(0, ...stats.data.activity.map((b) => b.count)),
                )}
              />
            </Card>

            <ResponsiveGrid columns={2}>
              <Card
                header={
                  <Card.Header
                    title="Demo komponent"
                    subtitle="ukázka pickerů (lokální stav)"
                  />
                }
              >
                <FormStack>
                  <DatePicker
                    label="Datum"
                    value={demoDate}
                    onChange={setDemoDate}
                  />
                  <TimePicker
                    label="Čas"
                    value={demoTime}
                    onChange={setDemoTime}
                  />
                  <DateTimePicker
                    label="Datum a čas"
                    value={demoDateTime}
                    onChange={setDemoDateTime}
                  />
                </FormStack>
              </Card>

              <Card
                header={
                  <Card.Header title="Systém" subtitle="informace o běhu" />
                }
              >
                <DescriptionList labelWidth={140}>
                  <DescriptionList.Item label="Verze">
                    {stats.data.system.version || "dev"}
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Spuštěno">
                    <DateTimeField record={stats.data.system} source="started_at" />
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Čas serveru">
                    <DateTimeField record={stats.data.system} source="server_now" />
                  </DescriptionList.Item>
                </DescriptionList>
              </Card>
            </ResponsiveGrid>
          </>
        ) : null}
      </Page.Content>
    </>
  );
}
